import { IAttrValue, OWDataType } from "@multiweb/types/contracts";
import {
    ExtendedPropertiesType,
    FileUtils,
    GetFormattedNumberStringParams,
    IBigNumber,
    INumberFormatInfo,
    INumberFormatOptions,
} from "@multiweb/types/multiweb";
import {
    AttrValue,
    BigNumberProxy,
    DataConverter,
    DateUtils,
    EnumUtils,
    FlagUtils,
    getExtendedPropertiesFromElement,
    Globalization,
    LocalizationUtils,
    NumberUtils,
    StringUtils,
    UrlUtils,
    UUID,
} from "./oldAppImports";

/**
 * Pomocná třída určená jako základ pro další implementace rozhraní {@link IBridge}.
 * Poskytuje přemostění na funkcionality z multiwebu, které jsou kompatibilní se striktním režimem
 * kompilace a zároveň nevyžadují komunikaci se serverem. Díky tomu je možné tento základ použít jak pro produkční implementaci tak pro implementace
 * sloužící unit testům a Storybooku.
 */
export abstract class StrictBridge {
    /**
     * Převede datum na řetězec.
     * @param date Datum
     * @param format Formát datumu
     */
    public readonly dateToString = DateUtils.dateToString;

    /**
     * Načtení formátovací funkce
     * @param format Formát
     * @param culture Nepovinný parametr culture, který určuje, zda bude pro tento konkrétní formát použita jiná cultura než globální
     */
    public readonly getDateToStringFunction = DateUtils.getDateToStringFunction;

    /** Počet milisekund v jednom dni */
    public readonly msPerDay = DateUtils.miliSecPerDay;

    /**
     * Zjistí, zda je zadané datum datem dnešním.
     * @param date Datum
     */
    public readonly isToday = (date: Date): boolean => (
        DateUtils.compareDates(date, new Date())
    );

    /**
     * Vytvoří instanci třídy **AttrValue** se zadanou hodnotou.
     * @param value Hodnota
     */
    public readonly getAttrValue = (value: any): IAttrValue => new AttrValue(value);

    /** Znak pro oddělování desetinných míst */
    public readonly decimalSeparator = Globalization.numberFormat.decimalSeparator;

    /** Znak pro oddělování skupin znaků v čísle */
    public readonly groupSeparator = Globalization.numberFormat.groupSeparator;

    /** Kód jazyka aplikace */
    // eslint-disable-next-line @typescript-eslint/naming-convention
    public readonly actualCultureISO639_1 = Globalization.actualCultureISO639_1;

    /** Vygeneruje univerzální unikátní identifikátor. */
    public readonly generateUUID = UUID.generate;

    /** @see StringUtils.compareCaseInsensitive */
    public readonly compareCaseInsensitive = StringUtils.compareCaseInsensitive;

    /** @see StringUtils.isNullOrEmpty */
    public readonly isNullOrEmpty = StringUtils.isNullOrEmpty;

    /** @see EnumUtils.getEnumValueFromValue */
    public readonly stringToEnumValue = EnumUtils.getEnumValueFromValue;

    /** @see FlagUtils.flagToCss */
    public readonly flagToCss = FlagUtils.flagToCss;

    /** @see EnumUtils.getEnumValueFromValue */
    public readonly valueToEnumValue = EnumUtils.getEnumValueFromValue;

    /** Zkombinuje pole podmínek */
    public readonly combineConditionsArray = StringUtils.combineConditionsArray;

    /**
     * Odstraní diakritiku z textu.
     * @param text Text, ze kterého se odstraní diakritika.
     */
    public readonly removeAccents = (text: string) => StringUtils.removeAccents(text);

    /**
     * Odstraní nebo nahradí HTML tagy v textu.
     * @param text Text, ve kterém se odstraní nebo nahradí HTML tagy.
     */
    public readonly stripHTMLFromText = (text: string) => StringUtils.stripHTMLFromText(text);

    /**
     * Aplikuje parametry na lokalizovaný řetězec.
     * @param text Lokalizovaný text s případnými placeholdery (např. `@0`).
     * @param params Parametry, které budou dosazeny za placeholdery.
     */
    public readonly applyLocParams = LocalizationUtils.applyLocParams;

    /**
     * Převede string na boolean.
     * @param string Obsahuje hodnotu pro parsovani.
     * @param boolean Obsahuje defaultní návratovou hodnotu v případě, že neprojde parsováním.
     */
    public readonly stringToBoolean = DataConverter.toBoolean;

    /**
     * Konvertuje string na číslo. Pokud je hodnota undefined vrací místo NaN vrací undefined.
     * @param value Obsahuje hodnotu pro parsování.
     */
    public readonly stringToNumberOrUndefined = DataConverter.toNumberOrUndefined;

    /**
     * Naformátuje zadanou hodnotu na základě zvolené formátovací funkce. Více informací v dokumentaci funkce {@link NumberUtils.getFormattedNumberString}
     * @param value Hodnota k formátování.
     * @param params Konfigurace formátování.
     * @param formatProviderKey Název klíče formátovací funkce
     */
    public readonly getFormattedNumberString = (value: string | IBigNumber | number | null | undefined, params: GetFormattedNumberStringParams, formatProviderKey: string): string => {
        return NumberUtils.getFormattedNumberString(value, params, formatProviderKey);
    };

    /**
     * Zjistí, zda je daný datový typ celé číslo
     * @param dataType Název číselného datového typu
     */
    public readonly isWholeNumberType = (dataType: OWDataType | string): boolean => {
        return NumberUtils.isWholeNumberType(dataType);
    };

    /**
     * Vrací výchozí formátování podle datového typu.
     */
    public readonly getDefaultFormat = (dataType?: OWDataType | string): string => {
        return NumberUtils.getDefaultFormat(dataType);
    };

    /**
     * Zjistí minimální a maximální hodnotu pro číselný datový typ nebo null, pokud nebylo nalezeno
     * @param dataType Název číselného datového typu
     */
    public readonly getTypeMinMax = (dataType: OWDataType | string): Record<"min" | "max", number> | null => {
        return NumberUtils.getTypeMinMax(dataType);
    };

    public readonly getExtendedPropertiesFromElement = (element: HTMLElement): ExtendedPropertiesType => {
        return getExtendedPropertiesFromElement(element);
    };

    public readonly getDefaultNumberFormat = (dataType?: OWDataType | string): string => {
        return NumberUtils.getDefaultFormat(dataType);
    };

    public readonly getNumberToString = (numStr: string, params: INumberFormatOptions): string => {
        return NumberUtils.numberToString(numStr, params);
    };

    public readonly removeFormatting = (value: string, groupSeparator: string, decimalSeparator: string): string => {
        return NumberUtils.removeFormatting(value, groupSeparator, decimalSeparator);
    };

    /**
     * Vytvoří novou instanci třídy {@link BigNumberProxy}.
     * @param value Hodnota.
     */
    public readonly createBigNumberProxy = (value: string | number): IBigNumber => {
        return new BigNumberProxy(value);
    };

    /**
     * Název souboru rozdělí na název a příponu
     * @param fileName název souboru k rozpadnutí
     */
    public readonly splitFileName = FileUtils.splitFileName;

    /**
     * Uživatelsky přívětivě zformátuje velikost souboru v bytech
     * @param fileSize Velikost souboru v bytech
     */
    public readonly prettyPrintFileSize = FileUtils.prettyPrintFileSize;

    /** Vrací informace o formátování čísla v aktuální kultuře. */
    public readonly getNumberFormat = (): INumberFormatInfo => {
        return Globalization.numberFormat;
    };

    /** @see StringUtils.replaceAll */
    public readonly replaceAll = StringUtils.replaceAll;

    /** @see StringUtils.replaceAllWithLookahead */
    public readonly replaceAllWithLookahead = StringUtils.replaceAllWithLookahead;

    public readonly isUrl = (value: string): boolean => {
        return UrlUtils.isUrl(value);
    };

    public readonly isParametricUrl = (value: string): boolean => {
        return UrlUtils.isParametricUrl(value);
    };

    public readonly isParametricMailto = (value: string): boolean => {
        return UrlUtils.isParametricMailto(value);
    };

    public readonly isEmail = (value: string): boolean => {
        return UrlUtils.isEmail(value);
    };

    public readonly isFile = (value: string): boolean => {
        return UrlUtils.isFile(value);
    };

    public readonly hasParameters = (value: string): boolean => {
        return UrlUtils.hasParameters(value);
    };
}
